import { useEffect, useRef } from 'react';
import motorAudioUrl from '@/assets/sound_effects/3Faz_Motor.wav';

export interface MotorSoundState {
  isSpinning: boolean;
  isStopping: boolean;
}

/**
 * Hook to play realistic 3-phase motor sound with seamless, gapless looping:
 * Uses dual HTML5 Audio instances (ping-pong double buffering) so that when one
 * audio reaches the end, the second audio is already playing before the first stops,
 * completely eliminating the browser seek gap/silence.
 *
 * @param isRunning whether the motor is active (commanded by PLC / simulation)
 * @param volume optional playback volume (0.0 to 1.0, default: 0.35)
 */
export function useMotorSound(isRunning: boolean, volume = 0.35): MotorSoundState {
  const audio1Ref = useRef<HTMLAudioElement | null>(null);
  const audio2Ref = useRef<HTMLAudioElement | null>(null);
  const activeIndexRef = useRef<1 | 2>(1);
  const nextLoopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRunningRef = useRef(isRunning);
  isRunningRef.current = isRunning;

  // Initialize both audio elements
  const getAudios = () => {
    if (typeof Audio === 'undefined') return null;
    if (!audio1Ref.current || !audio2Ref.current) {
      try {
        const a1 = new Audio(motorAudioUrl);
        const a2 = new Audio(motorAudioUrl);
        a1.preload = 'auto';
        a2.preload = 'auto';
        a1.volume = volume;
        a2.volume = volume;
        audio1Ref.current = a1;
        audio2Ref.current = a2;
      } catch {
        return null;
      }
    }
    return { a1: audio1Ref.current, a2: audio2Ref.current };
  };

  useEffect(() => {
    const audios = getAudios();
    if (!audios) return undefined;

    const { a1, a2 } = audios;

    try {
      a1.volume = volume;
      a2.volume = volume;
    } catch {}

    const stopAll = () => {
      if (nextLoopTimerRef.current) {
        clearTimeout(nextLoopTimerRef.current);
        nextLoopTimerRef.current = null;
      }
      try {
        a1.pause();
        a1.currentTime = 0;
      } catch {}
      try {
        a2.pause();
        a2.currentTime = 0;
      } catch {}
    };

    const scheduleNext = (currentAudio: HTMLAudioElement, nextAudio: HTMLAudioElement, nextIdx: 1 | 2) => {
      if (!isRunningRef.current) return;

      // Audio duration in ms (default fallback ~6650ms if metadata still loading)
      const durationMs = (currentAudio.duration && !isNaN(currentAudio.duration) && currentAudio.duration > 0)
        ? currentAudio.duration * 1000
        : 6650;

      // Start the next audio instance slightly before the current one ends (~80ms overlap)
      // This completely eliminates the browser seek gap/silence!
      const overlapMs = 80;
      const delayMs = Math.max(100, durationMs - overlapMs);

      nextLoopTimerRef.current = setTimeout(() => {
        if (!isRunningRef.current) return;

        try {
          nextAudio.currentTime = 0;
          const p = nextAudio.play();
          if (p !== undefined && typeof p.catch === 'function') {
            p.catch(() => {});
          }
        } catch {}

        activeIndexRef.current = nextIdx;
        const followingAudio = nextIdx === 1 ? a2 : a1;
        const followingIdx = nextIdx === 1 ? 2 : 1;
        scheduleNext(nextAudio, followingAudio, followingIdx);
      }, delayMs);
    };

    if (isRunning) {
      stopAll();
      activeIndexRef.current = 1;

      try {
        a1.currentTime = 0;
        const p = a1.play();
        if (p !== undefined && typeof p.catch === 'function') {
          p.catch(() => {});
        }
      } catch {}

      scheduleNext(a1, a2, 2);
    } else {
      stopAll();
    }

    return undefined;
  }, [isRunning, volume]);

  // Clean up completely on unmount
  useEffect(() => {
    return () => {
      if (nextLoopTimerRef.current) {
        clearTimeout(nextLoopTimerRef.current);
      }
      try {
        if (audio1Ref.current) {
          audio1Ref.current.pause();
          audio1Ref.current.currentTime = 0;
        }
        if (audio2Ref.current) {
          audio2Ref.current.pause();
          audio2Ref.current.currentTime = 0;
        }
      } catch {}
    };
  }, []);

  return {
    isSpinning: isRunning,
    isStopping: false,
  };
}
